import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Search, ChevronDown, AlertTriangle, Info, Sparkles } from "lucide-react";
import {
  SCENARIO_TABS, SCENARIOS, CATEGORIES, CATEGORY_COLORS,
  ROW_COLORS, NOTE_COLORS,
} from "../data/clinicalScenarios";

const TEAL = "#1D9E75";

export default function Drugs() {
  const [mainTab, setMainTab] = useState("reference");

  return (
    <div className="min-h-screen bg-white font-sans" style={{ fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif" }}>
      {/* Header */}
      <header className="border-b border-gray-200 px-6 lg:px-12 py-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-xs uppercase tracking-wider text-gray-500">Drugs.rx</div>
          <h1 className="text-2xl lg:text-3xl font-semibold mt-1 text-gray-900">Dental Pharmacology Reference</h1>
          {/* Top tabs */}
          <div className="mt-6 inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50">
            {[
              { id: "reference", label: "Drug reference" },
              { id: "scenarios", label: "Clinical scenarios" },
            ].map((t) => (
              <button
                key={t.id}
                data-testid={`drugs-tab-${t.id}`}
                onClick={() => setMainTab(t.id)}
                className={`px-5 py-2 text-sm font-medium rounded-md transition-colors ${
                  mainTab === t.id ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"
                }`}
                style={mainTab === t.id ? { color: TEAL } : undefined}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-8">
        {mainTab === "reference" ? <DrugReference /> : <ClinicalScenarios />}
      </div>
    </div>
  );
}

// ====================== DRUG REFERENCE ======================
function DrugReference() {
  const [drugs, setDrugs] = useState([]);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    api.get("/drugs").then((r) => setDrugs(r.data));
  }, []);

  const filtered = useMemo(() => {
    return drugs.filter((d) => {
      if (category !== "All" && d.category !== category) return false;
      if (query) {
        const q = query.toLowerCase();
        const hay = [d.name, d.brand, d.brand_names, d.indication, d.indications, d.category]
          .filter(Boolean).join(" ").toLowerCase();
        return hay.includes(q);
      }
      return true;
    });
  }, [drugs, query, category]);

  return (
    <div>
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
        <input
          data-testid="drugs-search-input"
          placeholder="Search by drug name, brand, or indication…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 bg-white focus:outline-none focus:border-[#1D9E75] focus:ring-1 focus:ring-[#1D9E75] text-sm"
        />
      </div>

      {/* Category pills */}
      <div className="mt-5 flex flex-wrap gap-2">
        {["All", ...CATEGORIES].map((c) => {
          const active = category === c;
          return (
            <button
              key={c}
              data-testid={`drugs-cat-${c.toLowerCase().replace(/[^a-z0-9]/g,'-')}`}
              onClick={() => setCategory(c)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                active ? "text-white border-transparent" : "text-gray-700 bg-white border-gray-200 hover:border-gray-300"
              }`}
              style={active ? { backgroundColor: TEAL } : undefined}
            >
              {c}
            </button>
          );
        })}
      </div>

      <div className="mt-4 text-xs text-gray-500" data-testid="drugs-count">
        {filtered.length} {filtered.length === 1 ? "drug" : "drugs"}
        {category !== "All" && <span> · {category}</span>}
      </div>

      {/* Drug cards */}
      <div className="mt-3 space-y-2">
        {filtered.map((d) => (
          <DrugCard key={d.id} drug={d} open={openId === d.id} onToggle={() => setOpenId(openId === d.id ? null : d.id)} />
        ))}
        {filtered.length === 0 && (
          <div className="text-sm text-gray-500 text-center py-12">No drugs match those filters.</div>
        )}
      </div>
    </div>
  );
}

function DrugCard({ drug, open, onToggle }) {
  const catColor = CATEGORY_COLORS[drug.category] || "bg-gray-50 text-gray-700 border-gray-200";
  const brand = drug.brand || drug.brand_names || "";
  const indication = drug.indication || drug.indications || "";
  const dosage = drug.dosage || drug.formulations || "";
  const notes = drug.clinical_notes || drug.key_facts || "";
  const interactions = drug.key_interactions || drug.dental_interactions || "";

  return (
    <div className="border border-gray-200 rounded-lg bg-white" data-testid={`drug-card-${drug.id}`}>
      <button
        onClick={onToggle}
        data-testid={`drug-toggle-${drug.id}`}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-gray-50 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900">{drug.name}</span>
              {brand && <span className="italic text-sm text-gray-500">({brand})</span>}
              {drug.is_new && (
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: TEAL }}>
                  NEW 2025
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className={`text-[11px] font-medium px-2.5 py-1 rounded-full border ${catColor}`}>
            {drug.category}
          </span>
          <ChevronDown size={18} className={`text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>

      {open && (
        <div className="px-5 pb-5 pt-1 border-t border-gray-100 space-y-3 text-sm" data-testid={`drug-body-${drug.id}`}>
          <Row label="Indication" value={indication} />
          <Row label="Dosage" value={dosage} mono />
          <Row label="Contraindications" value={drug.contraindications} />
          <Row label="Key interactions" value={interactions} />
          <Row label="Clinical notes" value={notes} accent />
        </div>
      )}
    </div>
  );
}

function Row({ label, value, mono, accent }) {
  if (!value) return null;
  return (
    <div className="grid grid-cols-[140px_1fr] gap-4 pt-2">
      <div className="text-xs font-medium uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`text-gray-800 leading-relaxed ${mono ? "font-mono text-[13px]" : ""}`} style={accent ? { color: "#0f4f3a" } : undefined}>
        {value}
      </div>
    </div>
  );
}

// ====================== CLINICAL SCENARIOS ======================
function ClinicalScenarios() {
  const [activeTab, setActiveTab] = useState(SCENARIO_TABS[0].id);
  const [openId, setOpenId] = useState(null);

  return (
    <div>
      {/* Penicillin allergy rule banner */}
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm" data-testid="pcn-rule-banner">
        <div className="flex items-start gap-3">
          <Info size={16} className="text-gray-500 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold text-gray-900">Penicillin allergy rule</div>
            <div className="mt-1 text-gray-700 leading-relaxed">
              <span className="font-medium">Non-anaphylactic</span> PCN allergy → <span className="font-medium" style={{ color: TEAL }}>Cephalexin (Keflex) FIRST</span>.
              <span className="mx-1">·</span>
              <span className="font-medium">Anaphylactic / severe</span> PCN allergy → <span className="font-medium" style={{ color: TEAL }}>Clindamycin FIRST</span>.
              <span className="mx-1">·</span>
              Never recommend cephalosporins for anaphylactic PCN allergy.
            </div>
          </div>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="mt-6 flex flex-wrap gap-2 border-b border-gray-200 pb-3 overflow-x-auto">
        {SCENARIO_TABS.map((t) => {
          const active = activeTab === t.id;
          return (
            <button
              key={t.id}
              data-testid={`scenario-tab-${t.id.toLowerCase().replace(/[^a-z]/g,'-')}`}
              onClick={() => { setActiveTab(t.id); setOpenId(null); }}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                active ? "text-white" : "text-gray-700 hover:bg-gray-100"
              }`}
              style={active ? { backgroundColor: TEAL } : undefined}
            >
              <span className="mr-1.5">{t.icon}</span>{t.id}
            </button>
          );
        })}
      </div>

      {/* Scenarios */}
      <div className="mt-5 space-y-3">
        {(SCENARIOS[activeTab] || []).map((s) => (
          <ScenarioCard key={s.id} scenario={s} open={openId === s.id} onToggle={() => setOpenId(openId === s.id ? null : s.id)} />
        ))}
      </div>
    </div>
  );
}

function ScenarioCard({ scenario, open, onToggle }) {
  return (
    <div className="border border-gray-200 rounded-lg bg-white" data-testid={`scenario-${scenario.id}`}>
      <button
        onClick={onToggle}
        data-testid={`scenario-toggle-${scenario.id}`}
        className="w-full px-5 py-4 flex items-center justify-between gap-4 text-left hover:bg-gray-50 rounded-lg"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-semibold text-gray-900">{scenario.title}</div>
            {scenario.badge && (
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: TEAL }}>
                {scenario.badge}
              </span>
            )}
          </div>
          {scenario.subtitle && <div className="text-xs text-gray-500 mt-0.5">{scenario.subtitle}</div>}
        </div>
        <ChevronDown size={18} className={`text-gray-400 transition-transform shrink-0 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-5 pb-5 pt-1 border-t border-gray-100 space-y-3" data-testid={`scenario-body-${scenario.id}`}>
          {scenario.notes_top?.map((n, i) => <Note key={`nt${i}`} {...n} />)}

          {scenario.kind === "ladder" ? (
            <div className="space-y-2 mt-2">
              {scenario.steps.map((step, i) => (
                <div key={`step-${step.level || i}`} className="flex gap-3 items-start border border-gray-200 rounded-lg p-3">
                  <div className="w-7 h-7 rounded-full text-white text-sm font-bold flex items-center justify-center shrink-0" style={{ backgroundColor: TEAL }}>
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm text-gray-900">{step.level}</span>
                      {step.badge && (
                        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full text-white" style={{ backgroundColor: TEAL }}>
                          {step.badge}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-700 mt-1 leading-relaxed">{step.text}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : scenario.kind === "cards" ? (
            <div className="space-y-3 mt-2">
              {scenario.cards_title && <div className="text-xs uppercase tracking-wider font-medium text-gray-500">{scenario.cards_title}</div>}
              <div className="grid sm:grid-cols-2 gap-2">
                {scenario.cards.map((c, i) => (
                  <div key={`card-${c.title || i}`} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                    <div className="font-medium text-sm text-gray-900">{c.title}</div>
                    {c.body && <div className="text-xs text-gray-600 mt-1 leading-relaxed">{c.body}</div>}
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {scenario.rows?.length > 0 && (
            <div className="space-y-2 mt-2">
              {scenario.rows.map((r, i) => <DrugRow key={`row-${r.name || i}`} row={r} />)}
            </div>
          )}

          {scenario.notes?.map((n, i) => <Note key={`n${i}`} {...n} />)}
        </div>
      )}
    </div>
  );
}

function DrugRow({ row }) {
  const palette = ROW_COLORS[row.color] || ROW_COLORS.green;
  return (
    <div className={`flex flex-col sm:flex-row gap-2 sm:items-start border ${palette.border} ${palette.bg} rounded-lg p-3 ${row.highlight ? "ring-1" : ""}`} style={row.highlight ? { boxShadow: `inset 0 0 0 1px ${TEAL}` } : undefined}>
      <span className={`shrink-0 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded ${palette.label}`} style={{ minWidth: 130, textAlign: "center" }}>
        {row.label}
      </span>
      <span className={`text-sm ${palette.text} leading-relaxed flex-1`}>{row.text}</span>
    </div>
  );
}

function Note({ color, text }) {
  const cls = NOTE_COLORS[color] || NOTE_COLORS.blue;
  const Icon = color === "amber" || color === "red" ? AlertTriangle : color === "green" ? Sparkles : Info;
  return (
    <div className={`rounded-lg border p-3 text-sm flex items-start gap-2 ${cls}`}>
      <Icon size={15} className="shrink-0 mt-0.5" />
      <div className="leading-relaxed">{text}</div>
    </div>
  );
}
